/**
 * @component AppLayout
 * @description 全站共用 Layout：可折疊側邊欄 + 頂部 Header
 * 所有受保護頁面皆使用此 Layout
 */
import { canAccessMemberPortalEditor } from './memberPortalAccess';
import { AppearanceToggle } from './DesignBoundary';
import { BrandIcon } from './BrandIcon';
import { buildNavItems, buildSidebarItems, memberSettingsPermissionPath } from './navItems';
import type { NavItem } from './navItems';
export { buildNavItems } from './navItems';
import { Fragment, useState, useEffect, useRef } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/auth.store.ts';
import { api, apiClient } from '../../lib/api.ts';
import { LanguageSwitcher } from '../layout/LanguageSwitcher.tsx';
import { useBrandingByKey } from '../../hooks/useBranding.ts';
import { usePermissions } from '../../hooks/usePermissions.ts';
import { useEditionStore } from '../../store/edition.store.ts';
import { useCurrentVenue } from '../../hooks/useCurrentVenue';
import { useSiteName } from '../../hooks/useSiteName.ts';
import type { FeatureKey } from '@pokerroom/types';
import { PATH_FEATURE_MAP } from '../../config/path-feature-map';
import { isNewerVersion } from '../../lib/versionUpdates';

export { isNewerVersion } from '../../lib/versionUpdates';

// ============================================================
// Nav 結構定義
// ============================================================
// ============================================================
// Helper
// ============================================================

/** 根據 edition feature flags 過濾（在 RBAC 過濾前執行） */
export function filterByEdition(
  items: NavItem[],
  hasFeature: (key: FeatureKey) => boolean,
): NavItem[] {
  return items
    .map((item) => {
      if (item.path) {
        const req = PATH_FEATURE_MAP[item.path];
        return req && !hasFeature(req) ? null : item;
      }
      const children = item.children?.filter((c) => {
        const req = PATH_FEATURE_MAP[memberSettingsPermissionPath(c.path)];
        return !req || hasFeature(req);
      });
      return children?.length ? { ...item, children } : null;
    })
    .filter(Boolean) as NavItem[];
}

/** The exact level-page grant is required because /tournament is a broad route prefix. */
export function canViewTimedLevelPage(visiblePages: string[] | null): boolean {
  return visiblePages?.includes('/tournament/timed-settlement-levels') ?? false;
}

/** 根據使用者權限過濾側邊欄項目 */
export function filterNavItems(
  items: NavItem[],
  visiblePages: string[] | null,
  allowedActions: string[] | null,
): NavItem[] {
  if (!visiblePages) return []; // 尚未載入，不顯示任何項目
  const allowed = new Set(visiblePages);
  return items
    .map((item) => {
      if (item.path) {
        return allowed.has(item.path) ? item : null;
      }
      // Group: 過濾子項
      const children = item.children?.filter(
        (c) =>
          allowed.has(memberSettingsPermissionPath(c.path)) ||
          (c.path === '/admin/member-portal-editor' &&
            canAccessMemberPortalEditor(visiblePages, allowedActions)) ||
          (c.path === '/tournament/payout-rules' && allowed.has('/tournament/templates')),
      );
      return children?.length ? { ...item, children } : null;
    })
    .filter(Boolean) as NavItem[];
}

function isActive(path: string, current: string): boolean {
  if (path === '/') return current === '/';
  if (path === '/admin/points-config') return current === path;
  if (
    path === '/admin/points-config/wallet/passes' &&
    current.replace(/\/+$/, '') === '/admin/points-config/wallet'
  )
    return true;
  return current === path || current.startsWith(path + '/');
}

/** Among siblings, only the longest matching path should be highlighted */
function bestMatchChild(siblings: { path: string }[], current: string): string | null {
  let best: string | null = null;
  for (const s of siblings) {
    if (isActive(s.path, current) && (!best || s.path.length > best.length)) {
      best = s.path;
    }
  }
  return best;
}

export function activeNavigationPath(items: NavItem[], current: string): string | null {
  return bestMatchChild(
    items.flatMap((item) => item.children ?? (item.path ? [{ path: item.path }] : [])),
    current,
  );
}

// ============================================================
// NavGroup — 可展開的分組
// ============================================================
function NavGroup({
  item,
  collapsed,
  location,
  activePath,
}: {
  item: NavItem;
  collapsed: boolean;
  location: string;
  activePath: string | null;
}) {
  const active = item.children?.some((child) => child.path === activePath) ?? false;
  const [open, setOpen] = useState(active);

  // 切換路由後若有子項匹配，自動展開
  useEffect(() => {
    setOpen(active);
  }, [active, location]);

  if (collapsed) {
    // 折疊模式：只顯示 icon，tooltip 用 title
    return (
      <div className="relative group">
        <button
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          title={item.label}
          className={`w-full min-h-11 flex items-center justify-center p-3 rounded-xl transition-colors ${
            active
              ? 'bg-primary/20 text-primary'
              : 'text-base-content/60 hover:bg-base-300 hover:text-base-content'
          }`}
        >
          <span className="text-lg">
            <BrandIcon value={item.icon} />
          </span>
        </button>
        {open &&
          item.children?.map((child) => (
            <Link
              key={child.path}
              to={child.path}
              title={child.label}
              aria-label={child.label}
              aria-current={child.path === activePath ? 'page' : undefined}
              className="flex min-h-11 items-center justify-center border-l-2 border-primary text-primary hover:bg-base-300"
            >
              <BrandIcon value={child.icon || '📄'} size={18} />
            </Link>
          ))}
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`w-full min-h-11 flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
          active
            ? 'bg-primary/15 text-primary'
            : 'text-base-content/70 hover:bg-base-300 hover:text-base-content'
        }`}
      >
        <span className="text-base">
          <BrandIcon value={item.icon} />
        </span>
        <span className="flex-1 text-sm font-medium text-left">{item.label}</span>
        <span
          aria-hidden="true"
          className={`text-xs transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
        >
          ›
        </span>
      </button>

      {open && (
        <div className="mt-1 ml-4 pl-3 border-l border-base-300 space-y-0.5">
          {(() => {
            const best = activePath;
            return item.children?.map((child, index) => (
              <Fragment key={child.path}>
                {child.section && child.section !== item.children?.[index - 1]?.section && (
                  <p className="px-2 pt-3 pb-1 text-xs font-medium text-base-content/60">
                    {child.section}
                  </p>
                )}
                <Link
                  to={child.path}
                  aria-current={child.path === best ? 'page' : undefined}
                  className={`flex min-h-11 items-center gap-2 px-2 py-2 rounded-lg text-sm transition-colors ${
                    child.path === best
                      ? 'bg-primary/20 text-primary font-medium'
                      : 'text-base-content/60 hover:bg-base-300 hover:text-base-content'
                  }`}
                >
                  {child.icon && (
                    <span className="text-xs">
                      <BrandIcon value={child.icon} size={16} />
                    </span>
                  )}
                  {child.label}
                </Link>
              </Fragment>
            ));
          })()}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Sidebar
// ============================================================
function Sidebar({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const location = useLocation().pathname;
  const user = useAuthStore((s) => s.user);
  const visiblePages = useAuthStore((s) => s.visiblePages);
  const allowedActions = useAuthStore((s) => s.allowedActions);
  const { t } = useTranslation('common');
  const { url: headerLogoUrl } = useBrandingByKey('header_logo');
  const isLite = useEditionStore((s) => s.isLite());
  const isTrialActive = useEditionStore((s) => s.isTrialActive);
  usePermissions(); // 載入權限資料到 store

  const siteName = useSiteName();
  const { data: navIconsSetting } = useQuery<{ key: string; value: string } | null>({
    queryKey: ['settings', 'nav_icons'],
    queryFn: async () => {
      try {
        return await api.get<{ key: string; value: string }>('/settings/nav_icons');
      } catch {
        return null;
      }
    },
    staleTime: 5 * 60 * 1000,
  });
  const iconOverrides: Record<string, string> | undefined = (() => {
    try {
      return navIconsSetting?.value ? JSON.parse(navIconsSetting.value) : undefined;
    } catch {
      return undefined;
    }
  })();
  const hasFeature = useEditionStore((s) => s.hasFeature);
  const allNavItems = buildNavItems(t, iconOverrides).map((item) =>
    item.children
      ? {
          ...item,
          children: item.children.filter(
            (child) =>
              child.path !== '/admin/member-portal-editor' ||
              allowedActions?.includes('member:portal_manage'),
          ),
        }
      : item,
  );
  const navItems = buildSidebarItems(
    filterNavItems(
      user?.isSystemUser ? allNavItems : filterByEdition(allNavItems, hasFeature),
      visiblePages,
      allowedActions,
    ),
    t,
    iconOverrides,
  );
  const activePath = activeNavigationPath(navItems, location);

  return (
    <aside
      data-collapsed={collapsed}
      className={`zipa-sidebar flex h-full flex-col bg-base-200 border-r border-base-300 transition-all duration-300 ${
        collapsed ? 'w-16' : 'w-60'
      } shrink-0`}
    >
      {/* Logo */}
      <div
        className={`flex items-center gap-2 py-4 border-b border-base-300 min-h-[64px] ${collapsed ? 'flex-col px-2' : 'px-4'}`}
      >
        {headerLogoUrl ? (
          <>
            <img src={headerLogoUrl} alt={siteName} className="h-8 w-8 object-contain" />
            {!collapsed && (
              <span className="zipa-sidebar-label font-bold text-base-content heading-font tracking-wide">
                {siteName}
              </span>
            )}
          </>
        ) : (
          <>
            <img
              src="/brand/zipa/assets/logo/mark-zipa-spade.png"
              alt={siteName}
              className="zipa-logo-light h-9 w-9 object-contain"
            />
            <img
              src="/brand/zipa/assets/logo/mark-zipa-spade-white.png"
              alt={siteName}
              className="zipa-logo-dark h-9 w-9 object-contain"
            />
            {!collapsed && (
              <span className="zipa-sidebar-label font-bold text-base-content heading-font tracking-wide">
                {siteName}
              </span>
            )}
          </>
        )}
        <button
          onClick={onToggle}
          className={`zipa-sidebar-toggle flex min-h-11 min-w-11 shrink-0 items-center justify-center text-base-content/60 hover:text-base-content transition-colors ${collapsed ? '' : 'ml-auto'}`}
          title={collapsed ? t('expandSidebar') : t('collapseSidebar')}
          aria-label={collapsed ? t('expandSidebar') : t('collapseSidebar')}
        >
          {collapsed ? '›' : '‹'}
        </button>
      </div>

      {/* Nav Items */}
      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5">
        {!visiblePages && navItems.length === 0 && (
          // Loading skeleton while permissions are being fetched
          <>
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2.5">
                <div className="w-5 h-5 rounded bg-base-300 animate-pulse" />
                {!collapsed && <div className="h-4 flex-1 rounded bg-base-300 animate-pulse" />}
              </div>
            ))}
          </>
        )}
        {navItems.map((item) => {
          if (item.path) {
            // Single link
            const active = item.path === activePath;
            return (
              <Link
                key={item.path}
                to={item.path}
                title={collapsed ? item.label : undefined}
                aria-current={active ? 'page' : undefined}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                  collapsed ? 'justify-center' : ''
                } ${
                  active
                    ? 'bg-primary/20 text-primary font-medium'
                    : 'text-base-content/70 hover:bg-base-300 hover:text-base-content'
                }`}
              >
                <span className="text-base">
                  <BrandIcon value={item.icon} />
                </span>
                {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
              </Link>
            );
          }
          return (
            <NavGroup
              key={item.iconKey}
              item={item}
              collapsed={collapsed}
              location={location}
              activePath={activePath}
            />
          );
        })}
      </nav>

      {/* User info */}
      <div className="border-t border-base-300 px-3 py-3">
        {collapsed ? (
          <div
            className="flex justify-center text-base-content/50 text-lg"
            title={user?.displayName ?? user?.username ?? ''}
          >
            {user?.displayName?.[0] || user?.username?.[0]}
          </div>
        ) : (
          <div className="flex items-center gap-2 px-1">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm font-bold shrink-0">
              {(user?.displayName ?? user?.username ?? '?')?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-base-content truncate flex items-center gap-1.5">
                <span>{user?.displayName ?? user?.username}</span>
                {isLite && (
                  <span
                    className={`text-[9px] px-1 py-px rounded font-medium ${isTrialActive ? 'bg-info/20 text-info' : 'bg-base-300 text-base-content/40'}`}
                  >
                    {isTrialActive ? 'Trial' : 'Lite'}
                  </span>
                )}
              </div>
              <div className="text-xs text-base-content/40 truncate">{user?.username}</div>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

// ============================================================
// Top Header
// ============================================================
function VersionUpdateBanner() {
  const { t } = useTranslation('common');
  const [dismissed, setDismissed] = useState(false);
  const { data: health } = useQuery<{ status: string; version: string }>({
    queryKey: ['health-version'],
    queryFn: () => fetch('/health').then((r) => r.json()),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (dismissed || !health?.version) return null;
  if (!isNewerVersion(health.version, __APP_VERSION__)) return null;

  return (
    <div
      data-testid="version-update-banner"
      className="bg-warning/15 border-b border-warning/30 px-4 py-1.5 flex items-center justify-center gap-3 text-sm text-warning"
    >
      <span>{t('updateAvailable')}</span>
      <button
        onClick={() => window.location.reload()}
        className="underline font-medium hover:opacity-80 transition-opacity"
      >
        {t('reloadNow')}
      </button>
      <button
        onClick={() => setDismissed(true)}
        className="ml-2 text-warning/60 hover:text-warning transition-colors leading-none"
        aria-label={t('close')}
      >
        ✕
      </button>
    </div>
  );
}

function TrialBanner() {
  const isLite = useEditionStore((s) => s.isLite());
  const isTrialActive = useEditionStore((s) => s.isTrialActive);
  const trialDaysRemaining = useEditionStore((s) => s.trialDaysRemaining);
  const isLoaded = useEditionStore((s) => s.isLoaded);
  const { t } = useTranslation('common');

  if (!isLoaded || !isLite) return null;

  // Trial active: show remaining days
  if (isTrialActive && trialDaysRemaining != null) {
    return (
      <div className="bg-info/15 border-b border-info/30 px-4 py-1.5 text-center text-sm text-info">
        {t('trial.active', {
          days: trialDaysRemaining,
          defaultValue: `免費試用期剩餘 ${trialDaysRemaining} 天 — 體驗完整 Business 功能`,
        })}
      </div>
    );
  }

  // Trial expired: show upgrade hint
  if (trialDaysRemaining === 0) {
    return (
      <div className="bg-warning/15 border-b border-warning/30 px-4 py-1.5 text-center text-sm text-warning">
        {t('trial.expired', { defaultValue: '試用期已結束 — 升級至 Business 以解鎖完整功能' })}
      </div>
    );
  }

  return null;
}

function TopHeader({ onOpenMenu }: { onOpenMenu: () => void }) {
  const { venue, isError, refetch } = useCurrentVenue();
  const navigate = useNavigate();
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const { t } = useTranslation('common');

  const logoutMutation = useMutation({
    mutationFn: () => apiClient.post('/auth/logout'),
    onSettled: () => {
      clearAuth();
      navigate('/login');
    },
  });

  return (
    <>
      <VersionUpdateBanner />
      <TrialBanner />
      <header className="min-h-16 bg-base-200 border-b border-base-300 flex flex-wrap items-center gap-2 sm:gap-3 px-4 md:px-6 py-2 shrink-0">
        <button
          type="button"
          onClick={onOpenMenu}
          aria-label="開啟導覽選單"
          aria-haspopup="dialog"
          className="sm:hidden min-h-11 px-3 rounded-lg bg-base-300 text-base-content"
        >
          選單
        </button>
        <div
          className="order-first w-full min-w-0 text-sm sm:order-none sm:w-auto sm:flex-1"
          aria-live="polite"
          data-testid="current-venue"
        >
          <div className="hidden sm:block text-xs text-base-content/60">
            {t('currentVenue', { defaultValue: '目前場館' })}
          </div>
          {venue ? (
            <div className="font-semibold break-words">
              {venue.name} <span className="font-mono text-base-content/60">({venue.code})</span>
            </div>
          ) : isError ? (
            <button type="button" onClick={() => void refetch()} className="text-error underline">
              {t('currentVenueUnavailable', { defaultValue: '無法確認場館，點此重試' })}
            </button>
          ) : (
            <span>{t('currentVenueLoading', { defaultValue: '確認場館中…' })}</span>
          )}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2 sm:gap-3">
          <AppearanceToggle />
          <LanguageSwitcher />
          <button
            onClick={() => logoutMutation.mutate()}
            disabled={logoutMutation.isPending}
            className="min-h-11 px-3 py-1.5 text-sm rounded-lg bg-base-300 text-base-content/70 hover:bg-error/20 hover:text-error transition-colors"
          >
            {t('logout')}
          </button>
        </div>
      </header>
    </>
  );
}

// ============================================================
// AppLayout
// ============================================================
export default function AppLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [tablet, setTablet] = useState(
    () => window.matchMedia('(min-width: 640px) and (max-width: 1023px)').matches,
  );
  useEffect(() => {
    const media = window.matchMedia('(min-width: 640px) and (max-width: 1023px)');
    const update = () => setTablet(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  const mobileMenu = useRef<HTMLDialogElement>(null);
  const location = useLocation();

  useEffect(() => {
    mobileMenu.current?.close();
  }, [location.pathname]);

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 640px)');
    const closeOnDesktop = () => {
      if (desktop.matches) mobileMenu.current?.close();
    };
    desktop.addEventListener('change', closeOnDesktop);
    return () => desktop.removeEventListener('change', closeOnDesktop);
  }, []);

  return (
    <div className="flex h-dvh overflow-hidden bg-base-100">
      <div className="zipa-desktop-sidebar hidden sm:flex shrink-0">
        <Sidebar
          collapsed={tablet || sidebarCollapsed}
          onToggle={() => setSidebarCollapsed((v) => !v)}
        />
      </div>
      <dialog
        ref={mobileMenu}
        aria-label="導覽選單"
        className="m-0 h-dvh max-h-none w-60 max-w-[85vw] border-0 p-0 bg-base-200 text-base-content backdrop:bg-black/60"
        onClick={(event) => {
          if (event.target === event.currentTarget || (event.target as HTMLElement).closest('a')) {
            mobileMenu.current?.close();
          }
        }}
      >
        <Sidebar collapsed={false} onToggle={() => mobileMenu.current?.close()} />
      </dialog>
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <TopHeader onOpenMenu={() => mobileMenu.current?.showModal()} />
        <main className="zipa-admin-content relative flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
